import { Observable } from 'rxjs';
import { tap } from 'rxjs';

import { BasicInterfaceTypeEnum } from '@shared-enums/cats.enum';
import { arrayIsTruthy, syncObjectProperties } from '@shared-helpers/array-object-helpers';
import { convertSapDateToMoment, convertSapDateToWeekDayString, getDayString } from '@shared-helpers/date-helpers';
import { safeCompare } from '@shared-helpers/string-number-helpers';
import { ISkfConfigEntity } from '@shared-interfaces/api';
import { IDayOfWeek } from '@shared-interfaces/dates';

import { ICatRecord } from '../interfaces/incoming';
import { ITableConfigEntity, ITextSummaryRow } from '../interfaces/internal-interfaces';
import { DatahubService } from '../services/datahub.service';
import { BasicInterface, IBasicInterfaceContext, IBasicInterfaceContract } from './basicInterface';
import { IOutgoingCatsArrays } from './catsDB';
import { ProductionTextRecord } from './ProductionTextRecord';
import { makeBillingProductionTextDailyConfig } from './table-configs/billing-tables.config';
import { makePayrollProductionTextDailyConfig } from './table-configs/payroll-tables.config';

export class ProductionText extends BasicInterface implements IBasicInterfaceContract {
  //calculated fields

  display: string;
  billingGrid: ITextSummaryRow[];
  payrollGrid: ITextSummaryRow[];
  detailPayneRecords: ProductionTextRecord[];
  costCenters: string[] = [];
  filteredCostCenter = '';

  //fields from sap
  CustGroup: string;
  CondGroup: string;
  SKF: string;
  ac_unit: string;
  Billcode: string;
  Type: string;

  //Descriptions - Payroll uses SKFDescription and everything else uses CustDesc
  CustDesc: string;
  SKFDescription: string;

  Printtocust: string;
  ShowOnScreen: string;
  Mode: string;

  readonly billCodeType = BasicInterfaceTypeEnum.ProductionText;

  constructor(
    public datahub: DatahubService,
    skf: ISkfConfigEntity,
    context: IBasicInterfaceContext,
  ) {
    super(context);
    syncObjectProperties(skf, this);
    this.setDisplayProperty();
  }

  addCostCenter(costCenter: string) {
    this.costCenters.push(costCenter);
  }

  private setDisplayProperty(): void {
    if (this.isPayroll) {
      this.display = this.SKFDescription;
    } else {
      this.display = this.CustDesc;
    }
  }

  setCatsRecords(catsRecords: ICatRecord[]): void {
    this.catsRecords = catsRecords.map((record) => {
      record.__costCenter = this.datahub.contracts.findContractByServiceOrder(record.RecOrder)?.getCostCenter();
      return record;
    });
    this.visibleCatsRecords = catsRecords;
    this.handleCatsRecordsSet();
  }

  handleCatsRecordsSet() {
    //once cats records are set, we should recalculate totals and re-render the object in the view. Keeping this to employee level should allow us to refresh just this entity when records are created destroyed or modified.
    this.calculateBillingGrid(this.filteredTimesheet);
    this.calculatePayrollGrid(this.filteredCostCenter);
    this.checkApprovalStatus(this.catsRecords);
  }

  filterByCostCenter(costCenter = '') {
    this.filteredCostCenter = costCenter;
    this.handleCatsRecordsSet();
  }

  filterByTimesheet(timesheetNo: string): void {
    this.show();
    if (!timesheetNo) {
      this.filteredTimesheet = '';
      this.calculateBillingGrid('');
      return;
    }
    this.filteredTimesheet = timesheetNo;
    this.calculateBillingGrid(timesheetNo);
    if (!arrayIsTruthy(this.billingGrid)) this.hide();
  }

  calculateBillingGrid(timesheetNo?: string) {
    let recordPool = this.visibleCatsRecords;
    if (timesheetNo) recordPool = recordPool.filter((x) => safeCompare(x.Extdocumentno, timesheetNo));
    this.billingGrid = [this.makeRow(recordPool)];
  }

  calculatePayrollGrid(costCenterNo?: string) {
    let recordPool = this.visibleCatsRecords;
    if (costCenterNo) recordPool = recordPool.filter((x) => safeCompare(x.__costCenter, costCenterNo));
    this.payrollGrid = [this.makeRow(recordPool)];
  }

  makeRow(records: ICatRecord[]): ITextSummaryRow {
    const row: ITextSummaryRow = this.makeEmptyTextRow();
    records.forEach((record) => {
      const workDateAsMoment = convertSapDateToMoment(record.Workdate);
      const weekDay = getDayString(workDateAsMoment);
      if (row[weekDay] === '') row[weekDay] = record.LongTextstr;
      else row[weekDay] += `, ${record.LongTextstr}`;
    });
    return row;
  }

  makeEmptyTextRow(): ITextSummaryRow {
    return {
      display: this.display,
      sun: '',
      mon: '',
      tue: '',
      wed: '',
      thu: '',
      fri: '',
      sat: '',
    };
  }

  getBillingDetailPayneValues(_row: number, column: ITableConfigEntity<IDayOfWeek>) {
    this.focusedDay = this.datahub.billingTimesheet.getDate(column.key);
    const records = this.visibleCatsRecords;
    this.detailPayneRecords = records
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new ProductionTextRecord(this, this.getBillingDetailPayneConfig()).fillStateFromSap(x));
    this.datahub.detailPayneHandler.showBillingProductionText(this);
  }

  getBillingDetailPayneConfig() {
    //when the detail payne is rendered for this employee, this function provides the appropriate drop down values.
    return makeBillingProductionTextDailyConfig();
  }

  launchPayrollDetailPayneHandler(row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    this.getPayrollDetailPayneValues(row, column);
    this.datahub.detailPayneHandler.showPayrollProduction(this);
  }

  getPayrollDetailPayneValues(_row: number, column: ITableConfigEntity<IDayOfWeek>): void {
    this.focusedDay = this.datahub.payrollTimesheet.getDate(column.key);
    this.detailPayneRecords = this.visibleCatsRecords
      .filter((record) => convertSapDateToWeekDayString(record.Workdate) === column.key)
      .map((x) => new ProductionTextRecord(this, this.getPayrollDetailPayneConfig()).fillStateFromSap(x));
  }

  getPayrollDetailPayneConfig() {
    return makePayrollProductionTextDailyConfig();
  }

  getPlaceholderEmployee() {
    return this.datahub.loggedInUserEmployeeId;
  }

  addNewRecord(config: ITableConfigEntity[]): ProductionTextRecord {
    const record = new ProductionTextRecord(this, config);
    return record.getRequiredObject(this);
  }

  save$(records: IOutgoingCatsArrays): Observable<ICatRecord[]> {
    return this.datahub.catsDB.syncSingleEntityWithCats$(records).pipe(
      tap((catsRecords: ICatRecord[]) => {
        catsRecords.forEach(
          (x) => (x.__costCenter = this.datahub.contracts.findContractByServiceOrder(x.RecOrder)?.getCostCenter()),
        );
        records.update
          .concat(records.delete)
          .forEach((record) => (this.catsRecords = this.catsRecords.filter((x) => x.Counter !== record.Counter)));
        this.catsRecords = this.catsRecords.concat(catsRecords);
        this.setCatsRecords(this.catsRecords);
      }),
    );
  }
}
